/**
 * ビューアが使うデザイントークン。
 *
 * 正本は [docs/design-tokens/](../../../../docs/design-tokens/README.md)
 * ([ADR-0009](../../../../docs/adr/0009-design-tokens-in-code.md))。
 *
 * **Shadow root には解決済みの値を埋め込む**([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md))。
 * CSS カスタムプロパティは Shadow 境界を継承して通るが、`:root { --sp-* }` の
 * **定義自体は Shadow root の中に存在しない**。したがって `:host` に定義する。
 *
 * **プリミティブ層は持ち込まない。** セマンティック層の値を解決済みで置く ——
 * 全 91 個を積むとバンドルが無駄に膨らむ(#33)。
 *
 * ここの値は手書きだが、**正本から解決した値と一致することをテストで
 * 検証している**([tokens.test.ts](./tokens.test.ts))。ずれたらテストが落ちる
 * ので、生成スクリプトを持たなくても乖離しない。
 */

/**
 * ビューアが使うトークン。キーは `tokens.json` の `semantic` のキー。
 *
 * **追加するときはテストも通ること**(値は正本から解決される)。
 */
export const VIEWER_TOKENS: Readonly<Record<string, string>> = {
  'surface-page': '#2b2b2f',
  'surface-raised': '#2c2c32',
  'surface-overlay-weak': 'rgba(255, 255, 255, 0.07)',
  'surface-overlay-strong': 'rgba(255, 255, 255, 0.2)',
  'border-default': '#3a3a3f',
  'border-strong': '#838383',
  'text-primary': '#ffffff',
  'text-secondary': '#8e8e96',
  'accent-primary': '#10b981',
  'accent-primary-surface': 'rgba(16, 185, 129, 0.1)',
  'accent-danger': '#ef4444',
};

/** 角丸。`tokens.json` の `radius`。 */
export const VIEWER_RADIUS = '8px';

/**
 * 埋め込み先に公開する調整の口。
 *
 * **v1 では 3 つだけ**([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md))。
 * 公開した口は後方互換の責務になるため意図的に絞る。背景色・文字色・
 * フォントを開けないのは、開けると「ダークモード基調」(設計書 7.6)の
 * 見え方が崩れ、**社内確認と外部表示の一致**(7.4)が壊れるため。
 *
 * 3 つ目「ホスト要素自体のサイズ・余白」は Shadow の外なので定義不要。
 */
export const PUBLIC_CUSTOM_PROPERTIES = ['--signa-radius', '--signa-accent'] as const;

/**
 * `:host` に置くトークン定義を組み立てる。
 *
 * **埋め込み先が `:root` に同名のプロパティを定義していても影響を受けない**
 * (`:host` の定義が Shadow 内で優先される)。
 */
export function tokenDeclarations(): string {
  const lines = Object.entries(VIEWER_TOKENS).map(([key, value]) => `  --sp-${key}: ${value};`);

  // 公開する口は「埋め込み先の指定があればそれ、なければ既定」にする。
  lines.push(`  --signa-radius: var(--signa-radius, ${VIEWER_RADIUS});`);
  lines.push(`  --signa-accent: var(--signa-accent, ${VIEWER_TOKENS['accent-primary'] ?? ''});`);

  return lines.join('\n');
}
